import { useEffect, useState } from 'react'
import { UserPlus, Send, CheckCircle, AlertTriangle } from 'lucide-react'
import { api } from '../services/api'
import styles from './CadastroColaboradores.module.css'

type TipoCadastro = 'interno' | 'externo'

const equipesPorTipo: Record<TipoCadastro, string[]> = {
  externo: ['Corretor Externo'],
  interno: ['Equipe PJ', 'Equipe SDR'],
}

export function CadastroColaboradores() {
  const [tipoCadastro, setTipoCadastro] = useState<TipoCadastro>('externo')
  const [formData, setFormData] = useState({
    idcorretor: '',
    nome: '',
    ramal: '',
    idusuariototalk: '',
    equipe: equipesPorTipo.externo[0],
  })
  const [submitting, setSubmitting] = useState(false)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  useEffect(() => {
    const options = equipesPorTipo[tipoCadastro]
    setFormData((prev) => ({
      ...prev,
      equipe: options.includes(prev.equipe) ? prev.equipe : options[0],
    }))
  }, [tipoCadastro])

  const handleChange = (field: keyof typeof formData) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }))
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitting(true)
    setSuccessMessage(null)
    setErrorMessage(null)

    try {
      const payload = {
        idcorretor: formData.idcorretor.trim(),
        nome: formData.nome.trim(),
        ramal: formData.ramal.trim(),
        idusuariototalk: formData.idusuariototalk.trim(),
        equipe: formData.equipe,
      }

      const result = await api.cadastrarColaborador(payload)

      if (result.success) {
        setSuccessMessage(result.message || 'Corretor cadastrado com sucesso!')
        setFormData({
          idcorretor: '',
          nome: '',
          ramal: '',
          idusuariototalk: '',
          equipe: equipesPorTipo[tipoCadastro][0],
        })
      } else {
        // Erro de validação do n8n
        const detalhes = result.detalhes ? ` - ${result.detalhes}` : ''
        setErrorMessage(`${result.message}${detalhes}`)
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao enviar cadastro'
      setErrorMessage(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Cadastro de Colaboradores</h1>
          <p className={styles.pageSubtitle}>
            Envie novos colaboradores para validacao e processamento automatico.
          </p>
        </div>
      </div>

      <section className={styles.formCard}>
        <div className={styles.formHeader}>
          <div className={styles.formHeaderIcon}>
            <UserPlus size={20} />
          </div>
          <h2 className={styles.formTitle}>Dados do colaborador</h2>
        </div>

        {successMessage && (
          <div className={`${styles.notice} ${styles.noticeSuccess}`}>
            <CheckCircle size={16} />
            <span>{successMessage}</span>
          </div>
        )}

        {errorMessage && (
          <div className={`${styles.notice} ${styles.noticeError}`}>
            <AlertTriangle size={16} />
            <span>{errorMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className={styles.formGrid}>
            <div className={styles.formGroup}>
              <label className={styles.label}>Tipo de cadastro</label>
              <select
                className={styles.select}
                value={tipoCadastro}
                onChange={(event) => setTipoCadastro(event.target.value as TipoCadastro)}
              >
                <option value="externo">Corretor Externo</option>
                <option value="interno">Corretor Interno</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Equipe</label>
              <select
                className={styles.select}
                value={formData.equipe}
                onChange={handleChange('equipe')}
                required
              >
                {equipesPorTipo[tipoCadastro].map((equipe) => (
                  <option key={equipe} value={equipe}>
                    {equipe}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>ID Corretor no CV</label>
              <input
                className={styles.input}
                type="text"
                value={formData.idcorretor}
                onChange={handleChange('idcorretor')}
                required
                placeholder="Informe o ID do corretor"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Nome do Corretor <span className={styles.optional}>(opcional)</span></label>
              <input
                className={styles.input}
                type="text"
                value={formData.nome}
                onChange={handleChange('nome')}
                placeholder="Para validacao com CV CRM"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Ramal MicroSIP</label>
              <input
                className={styles.input}
                type="text"
                value={formData.ramal}
                onChange={handleChange('ramal')}
                required
                placeholder="Informe o ramal"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>ID Usuario ToTalk</label>
              <input
                className={styles.input}
                type="text"
                value={formData.idusuariototalk}
                onChange={handleChange('idusuariototalk')}
                required
                placeholder="Informe o ID do usuario"
              />
            </div>
          </div>

          <div className={styles.actions}>
            <button className={styles.buttonPrimary} type="submit" disabled={submitting}>
              <Send size={16} />
              {submitting ? 'Enviando...' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </section>
    </div>
  )
}
